import { useEffect, useState, useRef } from 'react';
import type { FeatureCollection } from 'geojson';
import HoleMap from './components/HoleMap';
import { EMPTY_COURSE, loadCourse } from './lib/courses';
import { loadCartPaths } from './lib/cartpaths.ts';
import { initialState, step as detectStep, type DetectorState } from './lib/holeDetector';
import { useGps } from './lib/gps';
import { useSettings } from './lib/settings';
import { registerCarPlayCommands } from './lib/carplayBridge';
import { acceptCarSettings, type CarSettings } from './lib/settingsBridge.ts';
import type { Course } from './lib/types';
import type { MapControls } from './components/HoleMap';

/**
 * The CarPlay screen. Map content only.
 *
 * 🟥 WHY THERE IS NO CHROME HERE, AND WHY THAT IS NOT A COMPROMISE.
 *
 * Apple's navigation rule, verbatim: *"The base view must be used exclusively
 * to draw a map. Do not draw windows, alerts, panels, overlays, or user
 * interface elements in the base view."* The yardage cards, the HOLE 1 banner
 * and the PAR plate that /map shows are exactly that, and shipping them on the
 * car screen risks the entitlement — which is the single most valuable thing
 * this project owns.
 *
 * It is also pointless: *"Your app won't receive direct tap or drag events in
 * the base view."* Any button drawn here would be dead. Controls come from
 * CPMapButton and the panning interface, wired in CarPlaySceneDelegate and
 * lib/carplayBridge.
 *
 * What IS allowed is map content, and that is all of the interesting part: the
 * hole corridor, the greens, hazards, keep-out zones, cart paths, the route to
 * the next tee and the buggy itself. Everything the client actually liked when
 * he saw /map is drawn INTO the map, not floated over it, so all of it comes
 * across untouched.
 *
 * Fixes arrive from the native side through __gocaddyInjectFix (locationSource)
 * rather than from the browser's geolocation, because a WKWebView inside
 * CarPlay does not get a location permission of its own.
 */
export default function CarPlayApp() {
  const { settings } = useSettings();
  const [course, setCourse] = useState<Course>(EMPTY_COURSE);
  const [cartPaths, setCartPaths] = useState<FeatureCollection | null>(null);
  const [detector, setDetector] = useState<DetectorState>(() => initialState(1));
  const [controls, setControls] = useState<MapControls | null>(null);
  const [follow, setFollow] = useState(true);

  const { fix } = useGps({ simulate: null });

  /* What the phone told us to draw. Null until the delegate pushes, at which
     point it overrides this web view's own storage — the phone is the source of
     truth for settings, because it is the only surface a golfer can touch. */
  const [fromPhone, setFromPhone] = useState<CarSettings | null>(null);
  useEffect(() => acceptCarSettings(setFromPhone), []);

  // The car decides day/night, and it passes it in the URL. A white map at
  // night is a genuine hazard, so this is never the golfer's preference.
  useEffect(() => {
    const t = new URLSearchParams(location.search).get('theme');
    document.documentElement.dataset.theme = t === 'light' ? 'light' : 'dark';
    document.body.classList.add('carplay-host');
  }, []);

  useEffect(() => {
    const raw = fromPhone?.courseId ?? settings.courseId;
    const id = raw == null ? null : String(raw);
    if (id == null) return;
    let alive = true;
    void loadCourse(id).then(({ course: c }) => alive && setCourse(c));
    return () => { alive = false; };
  }, [settings.courseId, fromPhone?.courseId]);

  useEffect(() => {
    let alive = true;
    setCartPaths(null);
    if (!course.id) return;
    void loadCartPaths(course.id, course.centre).then((g) => alive && setCartPaths(g));
    return () => { alive = false; };
  }, [course.id, course.centre]);

  // Hole detection runs itself for the whole round. On a car screen with no
  // touch, an undetected hole is unrecoverable without reaching for the phone.
  useEffect(() => {
    if (settings.lockHoleNumber) return;
    if (!fix || fix.stale || !course.holes.length) return;
    setDetector((d) =>
      detectStep(d, {
        course,
        pos: fix.pos,
        accuracy: fix.accuracy,
        teeColour: settings.teeColour,
        now: fix.ts,
      })
    );
  }, [fix, course, settings.teeColour, settings.lockHoleNumber]);

  const holeIndex = Math.max(0, course.holes.findIndex((h) => h.number === detector.hole));
  const hole = course.holes.length ? course.holes[Math.min(holeIndex, course.holes.length - 1)] : null;

  const goHole = (i: number) => {
    const h = course.holes[Math.max(0, Math.min(i, course.holes.length - 1))];
    /*
     * 🟥 MANUAL WINS, and it has to say so. This used to set the hole and
     * confidence only. The detector effect above runs on every fix — dozens a
     * minute on the car, there is no distance filter — and re-detected the
     * hole from position, so the chevrons flicked to the next hole and the
     * next fix flicked it straight back. From the seat: "the arrow keys don't
     * work." manualUntil is the detector's own hold; set it, and the pick
     * stands for three minutes before position is allowed to argue.
     */
    if (h) setDetector((d) => ({
      ...d, hole: h.number, confidence: 1,
      manualUntil: Date.now() + 180_000, candidate: null, candidateSince: null,
    }));
  };

  /*
   * Following resumes by itself. On the car there is no touch to tap a
   * "recentre" button, and the CarPlay panning interface's Done button only
   * exists in pan mode — a finger drag on a touchscreen car has no Done. So
   * any pan breaks follow and arms a timer; twenty quiet seconds later the map
   * comes back to the buggy on its own.
   */
  const resumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const armResume = () => {
    if (resumeTimer.current) clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => setFollow(true), 20_000);
  };

  // Every control the car has. There is no on-screen fallback by design.
  useEffect(() => {
    if (!controls) return;
    return registerCarPlayCommands({
      holeNext: () => goHole(holeIndex + 1),
      holePrev: () => goHole(holeIndex - 1),
      zoomIn: controls.zoomIn,
      zoomOut: controls.zoomOut,
      zoomCycle: controls.zoomCycle,
      pan: (dx, dy) => { armResume(); controls.panBy(dx, dy); },
      panPx: (dx, dy) => { armResume(); controls.panPx(dx, dy); },
      panBegin: () => { setFollow(false); armResume(); },
      recentre: () => { if (resumeTimer.current) clearTimeout(resumeTimer.current); setFollow(true); },
    });
  }, [controls, holeIndex, course.holes.length]);

  return (
    <div className="cp-root">
      <HoleMap
        lockToHole={fromPhone?.lockToHole ?? settings.lockToHole}
        cartPaths={cartPaths}
        parked={null}
        course={course}
        hole={hole}
        teeColour={fromPhone?.teeColour ?? settings.teeColour}
        position={fix?.pos ?? null}
        heading={fix?.heading ?? 0}
        aim={null}
        onAimChange={() => {}}
        route={null}
        showHazards
        showCartPaths={(fromPhone?.indicators ?? settings.indicators).cartPaths !== false}
        follow={follow}
        onFollowBreak={() => setFollow(false)}
        fleet={[]}
        showFleet={false}
        playerUp
        mapMode={((fromPhone?.mapMode ?? settings.mapMode) === 'road'
          ? 'satellite'
          : (fromPhone?.mapMode ?? settings.mapMode)) as typeof settings.mapMode}
        theme={(document.documentElement.dataset.theme as 'dark' | 'light') ?? 'dark'}
        onControls={setControls}
        indicators={fromPhone?.indicators ?? settings.indicators}
      />
    </div>
  );
}
